import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { EVENEMENT_SESSION_PERDUE, isAuthenticated } from "@/api/client";
import LoginPage from "@/pages/LoginPage";
import OIDCCallback from "@/pages/OIDCCallback";
import Layout from "@/components/Layout";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const MultiViewPage = lazy(() => import("@/pages/MultiViewPage"));
const GeometryEditor = lazy(() => import("@/components/GeometryEditor"));
const IncidentsPage = lazy(() => import("@/pages/IncidentsPage"));
const MissionsPage = lazy(() => import("@/pages/MissionsPage"));
const MissionOfflinePage = lazy(() => import("@/pages/MissionOfflinePage"));
const ConflictResolutionPage = lazy(() => import("@/pages/ConflictResolutionPage"));
const UrgencyCardPage = lazy(() => import("@/pages/UrgencyCardPage"));
const CapacityPage = lazy(() => import("@/pages/CapacityPage"));
const DirectionPage = lazy(() => import("@/pages/DirectionPage"));
const OpsReportsPage = lazy(() => import("@/pages/OpsReportsPage"));
const V2MapPage = lazy(() => import("@/pages/V2MapPage"));
const RecherchePage = lazy(() => import("@/pages/RecherchePage"));
const ImportPage = lazy(() => import("@/pages/ImportPage"));
const InventairePage = lazy(() => import("@/pages/InventairePage"));
const InventaireAccueilPage = lazy(() => import("@/pages/InventaireAccueilPage"));
const AdministrationPage = lazy(() => import("@/pages/AdministrationPage"));
const ServiceMapPage = lazy(() => import("@/pages/ServiceMapPage"));
const QualiteDonneesPage = lazy(() => import("@/pages/QualiteDonneesPage"));
const IntelligencePage = lazy(() => import("@/pages/IntelligencePage"));
const NocPage = lazy(() => import("@/pages/NocPage"));
const ImpactSimulationPage = lazy(() => import("@/pages/ImpactSimulationPage"));

function App() {
  const [authed, setAuthed] = useState(isAuthenticated());

  // `isAuthenticated()` ne dit que « un jeton existe », pas « il est encore valide ». Quand le
  // serveur le refuse, l'application revient d'elle-meme a l'ecran de connexion au lieu de
  // laisser une coquille qui ne charge plus rien.
  useEffect(() => {
    const perdue = () => setAuthed(false);
    window.addEventListener(EVENEMENT_SESSION_PERDUE, perdue);
    return () => window.removeEventListener(EVENEMENT_SESSION_PERDUE, perdue);
  }, []);

  // Le retour de Keycloak passe AVANT la garde, et c'est tout l'enjeu : `/callback` est la
  // route qui FABRIQUE la session. Derrière la garde, elle était inatteignable — pas de
  // jeton, donc écran de connexion, donc pas d'échange du code, donc jamais de jeton. On se
  // connectait, on revenait sur /callback, et on relisait l'écran de connexion, sans fin.
  //
  // Le rendu se fait hors du gabarit : la barre de navigation interroge l'API, et on n'a
  // justement pas encore de jeton à lui donner.
  if (window.location.pathname === "/callback") {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/callback" element={<OIDCCallback />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (!authed) {
    return <LoginPage onLogin={() => setAuthed(true)} />;
  }

  return (
    <BrowserRouter>
      <Layout>
        <Suspense fallback={<p role="status">Chargement de la vue…</p>}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/callback" element={<OIDCCallback />} />
            <Route path="/carte-v2" element={<V2MapPage />} />
            {/* L'ancienne carte v1 est retirée : ses données venaient du greffon
                NetBox. Le raccourci reste, pour ne pas casser les liens partagés. */}
            <Route path="/carte" element={<Navigate to="/carte-v2" replace />} />
            <Route path="/recherche" element={<RecherchePage />} />
            <Route path="/incidents" element={<IncidentsPage />} />
            <Route path="/mission" element={<MissionsPage />} />
            <Route path="/mission/:id" element={<MissionOfflinePage />} />
            <Route path="/mission/:id/conflits" element={<ConflictResolutionPage />} />
            <Route path="/urgence" element={<UrgencyCardPage />} />
            <Route path="/capacite" element={<CapacityPage />} />
            <Route path="/carte-services" element={<ServiceMapPage />} />
            <Route path="/qualite-donnees" element={<QualiteDonneesPage />} />
            <Route path="/intelligence" element={<IntelligencePage />} />
            <Route path="/noc" element={<NocPage />} />
            <Route path="/direction" element={<DirectionPage />} />
            <Route path="/rapports" element={<OpsReportsPage />} />
            <Route path="/qualite" element={<Navigate to="/qualite-donnees" replace />} />
            <Route path="/reseau" element={<MultiViewPage />} />
            <Route path="/simulation-impact" element={<ImpactSimulationPage />} />
            {/* `/admin` appartient au frontal, qui le renvoie à Django : la console ne peut pas
                le servir, et l'entrée du menu tombait sur un 404 du backend. Le chemin de
                Django est une convention plus ancienne que cet écran ; c'est à la console
                de céder. L'ancienne adresse redirige, pour les signets déjà posés. */}
            <Route path="/inventaire" element={<InventaireAccueilPage />} />
            <Route path="/inventaire/:cle" element={<InventairePage />} />
            <Route path="/import" element={<ImportPage />} />
            <Route path="/administration" element={<AdministrationPage />} />
            <Route path="/admin" element={<Navigate to="/administration" replace />} />
            <Route path="/editer" element={<GeometryEditor />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
